import { BrandingProvider } from "@context/Branding";
import { initialize, PushMenu, teardown } from "admin-lte/dist/js/adminlte.esm.js";
import { ENV } from "config/ENV.config";
import { useEffect, useLayoutEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import AppHeader from "./AppHeader";
import AppSidebar from "./AppSidebar";
import { useColorMode } from "./useColorMode";

// AdminLTE's layout is driven by classes on <body>. They belong to this layout only: they are
// added on mount and removed on unmount, so the sign-in pages never inherit a sidebar offset.
const BODY_CLASSES = ["layout-fixed", "sidebar-expand-lg", "bg-body-tertiary"];

const SIDEBAR_BREAKPOINT = 991.98;

/** The signed-in console: AdminLTE 4 header + left sidebar + content + footer. */
const ConsoleLayout = () => {
  const { pathname } = useLocation();
  const { mode, setMode } = useColorMode();

  useLayoutEffect(() => {
    const body = document.body;
    body.classList.add(...BODY_CLASSES);
    // AdminLTE binds against the live DOM. Its own start-up pass ran at import, before React had
    // painted this shell, and found nothing; re-run it now. teardown() aborts the listeners it
    // registered, so a remount (StrictMode, logout/login) does not stack duplicates.
    initialize();
    return () => {
      teardown();
      body.classList.remove(...BODY_CLASSES, "sidebar-open", "sidebar-collapse");
    };
  }, []);

  // On a phone the sidebar is an overlay: close it once a link has been followed.
  useEffect(() => {
    const sidebar = document.querySelector<HTMLElement>(".app-sidebar");
    if (sidebar && window.innerWidth <= SIDEBAR_BREAKPOINT) PushMenu.getOrCreateInstance(sidebar).collapse();
  }, [pathname]);

  return (
    <BrandingProvider>
      <div className="app-wrapper">
        <AppHeader mode={mode} onMode={setMode} />
        <AppSidebar />
        {/* Each page brings its own content area (ConsolePage). */}
        <main className="app-main">
          <Outlet />
        </main>
        <footer className="app-footer">
          <div className="float-end d-none d-sm-inline">{ENV.supportEmail}</div>
          <strong>
            &copy; {new Date().getFullYear()} {ENV.brandName}.
          </strong>{" "}
          All rights reserved.
        </footer>
      </div>
    </BrandingProvider>
  );
};

export default ConsoleLayout;
